import React, { useState, useRef, useEffect } from "react"
import * as yup from "yup"
import { useNavigate, useLocation } from "react-router-dom"
import ROUTES from "../../router/routesConstants"
import { useUserContext } from "../../context/usercontext"
import axios from "axios"
import GLOBAL from "../../config/variable"

import { auth } from "../../firebase"
import {
  signInWithEmailAndPassword,
  sendEmailVerification,
  sendPasswordResetEmail,
} from "firebase/auth"

import logo from "../../assets/agencia-MD-logo.png"
import logoSiftia from "../../assets/logo-sifita.png"
import logoDinamica from "../../assets/logo-dinamica.png"

import { Formik, Form } from "formik"

// PrimeReact
import { Image } from "primereact/image"
import { InputText } from "primereact/inputtext"
import { Password } from "primereact/password"
import { Button } from "primereact/button"
import { FloatLabel } from "primereact/floatlabel"
import { ProgressSpinner } from "primereact/progressspinner"
import { Toast } from "primereact/toast"
import { Dialog } from "primereact/dialog"

const loginSchema = yup.object().shape({
  email: yup
    .string()
    .email("Invalid email format")
    .required("El email es requerido"),
  password: yup.string().required("La contraseña es requerida"),
})

function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const { setUser, setUserData } = useUserContext()
  const [loading, setLoading] = useState(false)
  const [needsVerification, setNeedsVerification] = useState(false)
  const [verificationEmail, setVerificationEmail] = useState("")
  const [verificationSent, setVerificationSent] = useState(false)
  const [storedPassword, setStoredPassword] = useState("")
  const [showResetPassword, setShowResetPassword] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  const [showDevelopersDialog, setShowDevelopersDialog] = useState(false)
  const toast = useRef(null)

  useEffect(() => {
    if (location.state?.registered) {
      const interval = setInterval(() => {
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Usuario Registrado, por favor verifique su correo",
          position: "top-center",
          life: 5000,
        })
        clearInterval(interval)
      }, 100)
      return () => clearInterval(interval)
    }
  }, [location.state])

  const handleResendVerification = async () => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        verificationEmail,
        storedPassword
      )
      await sendEmailVerification(userCredential.user)
      setVerificationSent(true)
    } catch (error) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail:
          "Error al enviar el correo de verificación, por favor inténtelo mas tarde.",
        position: "top-center",
        life: 5000,
      })
    }
  }

  const handleResetPassword = async (values, { setSubmitting }) => {
    setLoading(true)
    try {
      await sendPasswordResetEmail(auth, values.email)
      setResetSent(true)
      toast.current?.show({
        severity: "success",
        summary: "Éxito",
        detail: "Se ha enviado un correo para restablecer tu contraseña",
        position: "top-center",
        life: 5000,
      })
    } catch (error) {
      let errorMessage =
        "Error al restablecer la contraseña. Por favor, inténtelo de nuevo."
      const errorMap = {
        "auth/invalid-email": "El correo electrónico no es válido.",
        "auth/user-not-found":
          "No existe una cuenta con este correo electrónico.",
        "auth/too-many-requests":
          "Demasiados intentos. Por favor, inténtelo más tarde.",
        "auth/network-request-failed":
          "Error de conexión. Por favor, verifique su conexión a internet.",
        "auth/internal-error":
          "Error interno del servidor. Por favor, inténtelo más tarde.",
      }
      errorMessage = errorMap[error.code] || errorMessage

      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
        position: "top-center",
        life: 5000,
      })
    } finally {
      setLoading(false)
      setSubmitting(false)
    }
  }

  const handleLogin = async (values) => {
    try {
      setLoading(true)
      const userCredential = await signInWithEmailAndPassword(
        auth,
        values.email,
        values.password
      )
      const user = userCredential.user
      const response = await axios.get(
        `${GLOBAL.SERVER_URL}/users/${user.uid}`,
        {
          headers: {
            Authorization: `Bearer ${user.accessToken}`,
          },
        }
      )
      const userData = response.data.data

      if (!user.emailVerified) {
        setNeedsVerification(true)
        setVerificationEmail(user.email)
        setStoredPassword(values.password)
        return
      }

      window.dataLayer.push({
        event: "login",
        user_email: user.email,
        user_id: user.uid,
      })

      setUser(user)
      setUserData(userData)
      navigate(ROUTES.HOME)
    } catch (error) {
      let errorMessage =
        "Error al iniciar sesión. Por favor, inténtelo de nuevo."
      const errorMap = {
        "auth/invalid-credential":
          "Datos incorrectos. Por favor, verifique sus datos.",
        "auth/user-disabled":
          "Esta cuenta ha sido deshabilitada. Por favor, contacte a soporte.",
        "auth/user-not-found":
          "No existe una cuenta con este correo electrónico.",
        "auth/wrong-password":
          "Contraseña incorrecta. Por favor, verifique sus datos.",
        "auth/too-many-requests":
          "Demasiados intentos fallidos. Por favor, inténtelo más tarde.",
        "auth/network-request-failed":
          "Error de conexión. Por favor, verifique su conexión a internet.",
        "auth/internal-error":
          "Error interno del servidor. Por favor, inténtelo más tarde.",
      }
      errorMessage = errorMap[error.code] || errorMessage

      window.dataLayer.push({
        event: "login_error",
        error_code: error.code,
        error_message: errorMessage,
        user_email: values.email,
      })

      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: errorMessage,
        position: "top-center",
        life: 5000,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleBackToLogin = async () => {
    setNeedsVerification(false)
    setVerificationSent(false)
    setStoredPassword("")
    try {
      await auth.signOut()
    } catch (error) {
      console.error("Error al cerrar sesión:", error)
    }
  }

  const renderLoading = () => (
    <div className="flex flex-column align-items-center justify-content-center">
      <ProgressSpinner
        strokeWidth="4"
        animationDuration=".5s"
        style={{ width: "50px", height: "50px" }}
      />
      <p className="text-white mt-4">Cargando...</p>
    </div>
  )

  const renderResetPassword = () => (
    <div className="text-center">
      {resetSent ? (
        <>
          <p className="text-white mb-4">
            Se ha enviado un correo para restablecer tu contraseña.
          </p>
          <p className="text-white mb-4">
            Por favor, revisa tu bandeja de entrada.
          </p>
          <Button
            severity="info"
            label="Volver al login"
            onClick={() => {
              setShowResetPassword(false)
              setResetSent(false)
            }}
          />
        </>
      ) : (
        <Formik
          initialValues={{ email: "" }}
          validationSchema={yup.object().shape({
            email: yup
              .string()
              .email("Formato de email inválido")
              .required("El email es requerido"),
          })}
          onSubmit={handleResetPassword}
        >
          {({ handleChange, handleBlur, values, isSubmitting }) => (
            <div>
              <p className="text-white mb-4 text-sm">
                Ingresa tu correo electrónico para restablecer tu contraseña.
              </p>
              <Form className="formLogin">
                <div className="flex flex-column my-5">
                  <FloatLabel>
                    <InputText
                      id="email"
                      name="email"
                      type="email"
                      className="text-white"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                    <label className="text-white" htmlFor="email">
                      Correo
                    </label>
                  </FloatLabel>
                </div>
                <div className="flex flex-column gap-2 mb-6">
                  <Button
                    severity="primary"
                    label="Enviar correo"
                    type="submit"
                    disabled={isSubmitting}
                  />
                  <Button
                    severity="info"
                    label="Volver"
                    onClick={() => setShowResetPassword(false)}
                  />
                </div>
              </Form>
            </div>
          )}
        </Formik>
      )}
    </div>
  )

  const renderVerificationNotice = () => {
    if (verificationEmail) {
      window.dataLayer.push({
        event: "verification_notice",
        user_email: verificationEmail,
      })
    }
    return (
      <div className="text-center">
        <p className="text-white mb-4 text-sm">
          Por favor, verifica tu correo electrónico antes de iniciar sesión.
        </p>
        <p className="text-white mb-4 text-sm">
          Se ha enviado un correo de verificación a: {verificationEmail}
        </p>
        {verificationSent ? (
          <p className="text-white text-sm mb-4">
            ¡Correo de verificación enviado!
          </p>
        ) : (
          <Button
            severity="primary"
            label="Reenviar correo de verificación"
            onClick={handleResendVerification}
          />
        )}
        <p className="text-white mb-4 text-sm">
          Si no recibiste el correo, por favor, verifica tu bandeja de spam o
          intenta nuevamente.
        </p>
        <Button
          severity="info"
          label="Volver al login"
          onClick={handleBackToLogin}
        />
      </div>
    )
  }

  const renderLoginForm = () => (
    <Formik
      initialValues={{ email: "", password: "" }}
      validationSchema={loginSchema}
      onSubmit={handleLogin}
    >
      {({
        handleChange,
        handleBlur,
        values,
        isSubmitting,
        touched,
        errors,
      }) => (
        <Form className="formLogin flex flex-column gap-6 mb-4">
          <div className="flex flex-column">
            <FloatLabel>
              <InputText
                id="email"
                name="email"
                type="email"
                className="text-white"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <label className="text-white " htmlFor="email">
                Correo
              </label>
            </FloatLabel>
            {touched.email && errors.email && (
              <p className="form-text-err">{errors.email}</p>
            )}
          </div>

          <div className="flex flex-column">
            <FloatLabel>
              <Password
                id="password"
                name="password"
                toggleMask
                className="text-white"
                feedback={false}
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
              />
              <label className="text-white" htmlFor="password">
                Contraseña
              </label>
            </FloatLabel>
            {touched.password && errors.password && (
              <p className="form-text-err">{errors.password}</p>
            )}
          </div>

          <div className="flex flex-column align-center justify-content-center gap-3">
            <Button
              label="Iniciar sesión"
              type="submit"
              disabled={isSubmitting}
            />
            <Button
              onClick={(e) => {
                e.preventDefault()
                return navigate(ROUTES.REGISTER)
              }}
              type="button"
              severity="secondary"
              label="Crear cuenta"
            />
          </div>

          <div className="flex flex-column align-center justify-content-center gap-3">
            <Button
              className="bg-transparent border-0 text-s cursor-pointer"
              style={{
                color: "var(--secondary-color)",
                textDecoration: "underline",
                display: "inline-block",
              }}
              onClick={() => setShowResetPassword(true)}
              label="Restablecer contraseña"
            />
          </div>
        </Form>
      )}
    </Formik>
  )

  return (
    <div className="login-container">
      <Toast ref={toast} position="top-center" life={5000} />
      <div className="flex flex-column items-center">
        <Image className="text-center my-3" src={logo} alt="Logo" />
      </div>
      <hr />

      <h3 className="text-white text-center text-3xl">
        {showResetPassword ? "Restablecer contraseña" : "¡Bienvenido!"}
      </h3>

      <div id="loginform">
        {loading
          ? renderLoading()
          : showResetPassword
          ? renderResetPassword()
          : needsVerification
          ? renderVerificationNotice()
          : renderLoginForm()}
      </div>

      <div className="text-center mt-6">
        <Button
          label="Desarrollado por"
          severity="info"
          size="small"
          onClick={() => setShowDevelopersDialog(true)}
        />
      </div>

      <Dialog
        header="Programa de Lealtad"
        visible={showDevelopersDialog}
        onHide={() => setShowDevelopersDialog(false)}
        style={{ width: "90%", maxWidth: "500px" }}
        breakpoints={{ "960px": "75vw", "641px": "90vw" }}
        className="text-center"
      >
        <div>
          <div>
            <p>
              El <strong>Aplicativo de Programa Lealtad</strong> fue
              desarrollado en colaboración entre Siftia y Dinámica Consultores,
              con el objetivo de proporcionar una solución innovadora para
              premiar la fidelidad de los clientes de MD Tours.
            </p>
            <p>
              Esta plataforma permite gestionar de manera eficiente y
              personalizada los puntos acumulados por los clientes, incentivando
              su participación y promoviendo una mayor lealtad hacia la marca.
            </p>
          </div>
          <div className="flex justify-content-center flex-wrap align-content-center gap-4 my-5">
            <Image src={logoSiftia} alt="Logo 1" width="160" />
            <Image src={logoDinamica} alt="Logo 2" width="160" />
          </div>
          <a
            href="https://www.dcicr.com/ProgramasDeLealtad/"
            target="_blank"
            className="text-blue"
            rel="noreferrer"
            onClick={() => setShowDevelopersDialog(false)}
          >
            Solicitar Información
          </a>
        </div>
      </Dialog>
    </div>
  )
}

export default Login
